<template>
    <div>
        <!-- Desktop/Tablet Layout -->
        <v-card 
            v-if="!isMobile"
            class="mb-4 scrollable-card"
        >
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ title }}</span>
                <div class="d-flex align-center">
                    <v-text-field 
                        v-if="showSearch"
                        v-model="searchInput"
                        :label="t('search')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        prepend-inner-icon="mdi-magnify"
                        class="mr-2"
                        style="max-width: 600px; min-width: 400px;"
                    />
                    
                    <slot name="toolbar-actions" :items="items" />
                </div>
            </v-card-title>

            <v-card-text>
                <v-skeleton-loader
                    v-if="!showTable"
                    type="table"
                    class="elevation-0"
                    :loading="!showTable"
                    :width="'100%'"
                    :height="'400px'"
                />

                <v-data-table-server
                    v-if="showTable"
                    v-model:items-per-page="itemsPerPage"
                    :headers="computedHeaders"
                    :items="items"
                    :items-length="totalItems"
                    :loading="loading"
                    :search="search"
                    :sort-by="sort"
                    :items-per-page-options="itemsPerPageOptions"
                    :item-value="itemValue"
                    @update:options="loadItems"
                >
                    <template v-slot:item="{ item, columns }">
                        <slot name="desktop-row" :item="item" :columns="columns">
                            <tr>
                                <td v-for="column in columns" :key="column.key">
                                    <slot :name="`item.${column.key}`" :item="item" :value="getNestedValue(item, column.key)">
                                        {{ getNestedValue(item, column.key) }}
                                    </slot>
                                </td>
                            </tr>
                        </slot>
                    </template>

                    <!-- Pass through any additional slots -->
                    <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
                        <slot :name="slot" v-bind="scope" />
                    </template>
                </v-data-table-server>
            </v-card-text>
        </v-card>

        <!-- Mobile Layout -->
        <v-card 
            v-if="isMobile"
            class="mb-4 scrollable-card"
        >
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ title }}</span>
                <div class="d-flex align-center">
                    <v-text-field 
                        v-if="showSearch"
                        v-model="searchInput"
                        :label="t('search')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        prepend-inner-icon="mdi-magnify"
                        class="mr-2"
                        style="min-width: 110px;"
                    />
                    
                    <slot name="mobile-toolbar-actions" :items="items" />
                </div>
            </v-card-title>

            <v-card-text>
                <v-skeleton-loader
                    v-if="!showTable"
                    type="table"
                    class="elevation-0"
                    :loading="!showTable"
                    :width="'100%'"
                    :height="'400px'"
                />

                <v-data-table-server 
                    v-if="isMobile && showTable"
                    class="elevation-0 mobile-data-table"
                    :expand-on-click="false"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                    v-model:items-per-page="itemsPerPage"
                    :headers="mobileHeaders"
                    :items="items"
                    :items-length="totalItems"
                    :loading="loading"
                    :search="search"
                    :sort-by="sort"
                    :items-per-page-options="itemsPerPageOptions"
                    :item-value="itemValue"
                    @update:options="loadItems"
                    :mobile="true"
                >
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>
                                <slot name="mobile-item" :item="item">
                                    <v-card class="mb-2" variant="flat">
                                        <v-card-text>
                                            {{ JSON.stringify(item) }}
                                        </v-card-text>
                                    </v-card>
                                </slot>
                            </td>
                        </tr>
                    </template>
                </v-data-table-server>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useDebounce } from '@/utils/useDebounce';

const props = defineProps({
    title: {
        type: String,
        default: 'Data Grid'
    },
    headers: {
        type: Array,
        required: true
    },
    queryBuilder: {
        type: Function,
        required: true
    },
    itemValue: {
        type: String,
        default: 'id'
    },
    showSearch: {
        type: Boolean,
        default: true
    },
    itemsPerPageOptions: {
        type: Array,
        default: () => [
            { title: '50', value: 50 },
            { title: '100', value: 100 },
            { title: '150', value: 150 },
            { title: '200', value: 200 }
        ]
    },
    defaultSort: {
        type: Array,
        default: () => []
    },
    filterContext: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['items-loaded', 'error']);

const store = useStore();
const { t } = useI18n();
const { mobile, mdAndUp } = useDisplay();
const isMobile = computed(() => mobile.value && !mdAndUp.value);

const conn = store.getters.getDBConnection;

// State
const loading = ref(false);
const items = ref([]);
const totalItems = ref(0);
const page = ref(1);
const itemsPerPage = ref(10);
const showTable = ref(false);
const searchInput = ref('');
const searchQuery = useDebounce(searchInput, 500);
const sort = ref(props.defaultSort);

// Computed
const computedHeaders = computed(() => {
    return props.headers.map(header => ({
        ...header,
        title: header.titleKey ? t(header.titleKey) : header.title
    }));
});

const mobileHeaders = [
    { title: 'Items', key: 'items', sortable: false }
];

const search = computed(() => searchQuery.value);

// Methods
const getNestedValue = (obj, path) => {
    return path.split('.').reduce((o, p) => o?.[p], obj);
};

const loadItems = async (options = {}) => {
    loading.value = true;
    
    try {
        const currentPage = options.page || page.value;
        const currentItemsPerPage = options.itemsPerPage || itemsPerPage.value;
        let currentSort = sort.value[0] || {};
        const searchTerm = searchQuery.value;

        if (options.sortBy && options.sortBy.length) {
            currentSort = {
                key: options.sortBy[0].key,
                order: options.sortBy[0].order
            };
            sort.value = [currentSort];
        }

        // Call the queryBuilder function to get the queries
        const queries = await props.queryBuilder({
            page: currentPage,
            itemsPerPage: currentItemsPerPage,
            sort: currentSort,
            search: searchTerm,
            filterContext: props.filterContext
        });

        // Execute count query
        const countResult = await conn.query(queries.countQuery);
        let myTotalItems = 0;
        if (countResult.numRows > 0) {
            myTotalItems = Number(countResult.toArray()[0].toJSON().total);
        }

        // Execute items query
        const itemsResult = await conn.query(queries.itemsQuery);
        const rawItems = itemsResult.toArray().map(row => row.toJSON());

        // Apply custom transformation if provided
        const transformedItems = queries.transformItems 
            ? queries.transformItems(rawItems) 
            : rawItems;

        items.value = transformedItems;
        totalItems.value = myTotalItems;
        page.value = currentPage;
        itemsPerPage.value = currentItemsPerPage;

        emit('items-loaded', { items: transformedItems, totalItems: myTotalItems });
    } catch (error) {
        console.error('Error loading data:', error);
        emit('error', error);
    } finally {
        loading.value = false;
    }
};

// Watchers
watch(searchQuery, () => {
    // Just reset page to 1, let the v-data-table-server handle the reload via @update:options
    page.value = 1;
});

watch(
    () => props.filterContext.filtersLastUpdated,
    () => {
        setTimeout(() => {
            loadItems({ page: 1 });
        }, 100);
    },
    { deep: true }
);

// Lifecycle
onMounted(() => {
    showTable.value = true;
});

// Expose methods for parent components
defineExpose({
    loadItems,
    items
});
</script>

<style>
.v-data-table .v-icon {
    opacity: 0.6;
}

.mobile-data-table > .v-table__wrapper > table > tbody > tr > td {
    padding: 0 !important;
}

.mobile-data-table .v-card-title,
.mobile-data-table .v-card-text {
    padding: 5px 0 !important;
}

.v-data-table-headers--mobile {
    display: none !important;
}
</style>